import { AddressMethod, AddressSearchValue } from '@qbe/dfc-smart-ui';
import type { AddressList } from '@qbe/dfc-services';
import { ADDRESS_FIELD_MAPS } from './addressHelpers';
import type { AddressType } from './addressTypes';

interface ResolveManualAddressInput {
  hasLookupSelection: boolean;
  resolvedLookupAddress: AddressSearchValue;
}

export interface ProcessAddressChangeParams {
  type: AddressType;
  addressList: AddressList | null;
  structured: AddressSearchValue | null;
  method: AddressMethod;
  resolveManualAddress: ResolveManualAddressInput;
}

export interface ProcessAddressChangeData {
  address: string | null;
  addressObject: AddressList | null;
  streetAddress: string;
  suburb: string;
  state: string;
  postcode: string;
  country: string;
  isAddressValid: boolean;
  isManualAddress: boolean;
  isManualAddressValid?: boolean;
  applyChanges: boolean;
  shouldRemount: boolean;
}

const toLookupData = (
  addressList: AddressList | null,
  structured: AddressSearchValue | null
): ProcessAddressChangeData => ({
  address: addressList?.partialAddress ?? null,
  addressObject: addressList ?? null,
  streetAddress: [structured?.buildingName, structured?.streetAddress1].filter((str) => str && str.length).join(', '),
  suburb: structured?.city ?? '',
  state: structured?.state ?? '',
  postcode: structured?.postcode ?? '',
  country: structured?.country ?? 'Australia',
  isAddressValid: !!structured,
  isManualAddress: false,
  applyChanges: true,
  shouldRemount: false,
});

const toManualData = (structured: AddressSearchValue | null, shouldRemount = false): ProcessAddressChangeData => ({
  address: null,
  addressObject: null,
  streetAddress: structured?.streetAddress1 ?? '',
  suburb: structured?.city ?? '',
  state: structured?.state ?? '',
  postcode: structured?.postcode ?? '',
  country: structured?.country ?? '',
  isAddressValid: false,
  isManualAddress: true,
  applyChanges: true,
  shouldRemount,
});

const toNoopData = (): ProcessAddressChangeData => ({
  address: null,
  addressObject: null,
  streetAddress: '',
  suburb: '',
  state: '',
  postcode: '',
  country: '',
  isAddressValid: false,
  isManualAddress: true,
  applyChanges: false,
  shouldRemount: false,
});

export const processAddressChange = ({
  type,
  addressList,
  structured,
  method,
  resolveManualAddress,
}: ProcessAddressChangeParams): ProcessAddressChangeData => {
  // Guard against invalid type values and preserve existing no-op behavior.
  if (!ADDRESS_FIELD_MAPS[type]) {
    return toNoopData();
  }

  if (method === AddressMethod.Lookup) {
    return toLookupData(addressList, structured);
  }

  if (method === AddressMethod.Manual) {
    if (structured) {
      return toManualData(structured);
    }

    if (!resolveManualAddress.hasLookupSelection) {
      return toNoopData();
    }

    // Carry over resolved lookup fields when switching to manual mode.
    return toManualData(resolveManualAddress.resolvedLookupAddress, true);
  }

  return toNoopData();
};
